import React, { createContext, useContext, useState, useCallback } from 'react';

const TripPlannerTutorialContext = createContext();

export const useTripPlannerTutorial = () => {
  const context = useContext(TripPlannerTutorialContext);
  if (!context) {
    throw new Error('useTripPlannerTutorial must be used within a TripPlannerTutorialProvider');
  }
  return context;
};

// Local storage key for tracking tutorial completion
const TRIP_TUTORIAL_COMPLETED_KEY = 'vibary_trip_planner_tutorial_completed';

// Trip Planner tutorial step definitions - Informational flow (no required actions)
export const TRIP_TUTORIAL_STEPS = [
  // === INTRO ===
  {
    id: 'trip-intro',
    title: 'Welcome to Trip Planner',
    content: 'Organize your places into days and time slots. Let\'s take a quick look around!',
    target: null,
    placement: 'center',
  },

  // === DATE SELECTION ===
  {
    id: 'trip-date',
    title: 'Set Your Trip Dates',
    content: 'Tap here to set when your trip starts. Each day will show the actual date.',
    target: '[aria-label="Change start date"]',
    placement: 'bottom',
  },

  // === DAYS NAVIGATION ===
  {
    id: 'trip-days-nav',
    title: 'Navigate Between Days',
    content: 'Jump to any day, or tap + to add more days to your trip.',
    target: '.trip-planner-days-nav-buttons',
    placement: 'bottom',
  },

  // === DAY BUCKETS & TIME SLOTS ===
  {
    id: 'trip-day-buckets',
    title: 'Organize by Time of Day',
    content: 'Each day has Morning, Afternoon, and Evening slots. Drag places into the time that works best.',
    target: '.day-bucket',
    targetFallback: '.trip-planner-content',
    placement: 'top',
  },

  // === DRAG & DROP ===
  {
    id: 'trip-drag-drop',
    title: 'Drag & Drop',
    content: 'Press and hold any place, then drag to rearrange or move it between days.',
    target: '.trip-place-card',
    targetFallback: '.day-bucket',
    placement: 'top',
  },

  // === UNSCHEDULED PLACES ===
  {
    id: 'trip-unscheduled',
    title: 'Not Yet Scheduled',
    content: 'Places you haven\'t assigned appear here. Drag them up to schedule, or keep them as backup options.',
    target: '.unassigned-bucket-header',
    placement: 'top',
  },

  // === HOURS INFO ===
  {
    id: 'trip-hours',
    title: 'Opening Hours',
    content: 'See which days a place is closed. Once you schedule it, this shows that day\'s hours instead.',
    target: '.trip-place-hours',
    placement: 'top',
  },

  // === SUGGESTIONS ===
  {
    id: 'trip-suggestions',
    title: 'Get Suggestions',
    content: 'Tap here for suggestions based on time of day and proximity to places you\'ve already scheduled.',
    target: '[aria-label="Show suggestions"], [aria-label="Exit suggestions"]',
    placement: 'bottom',
  },

  // === DIRECTIONS ===
  {
    id: 'trip-directions',
    title: 'Get Directions',
    content: 'When you have two or more places scheduled, tap the directions button between them to open routes in Google Maps.',
    target: null,
    placement: 'center',
    illustration: 'directions',
  },

  // === MAP VIEW ===
  {
    id: 'trip-map',
    title: 'Map View',
    content: 'See your scheduled places on a map, organized by day and numbered in itinerary order.',
    target: '[aria-label="Map view"]',
    placement: 'bottom',
  },

  // === TRIP NOTES ===
  {
    id: 'trip-notes',
    title: 'Trip Notes',
    content: 'Tap + to add notes like reservation numbers, check-in times, or reminders.',
    target: '.time-slot-add-note-btn',
    targetFallback: '.time-slot-header',
    placement: 'bottom',
  },

  // === PRIVACY SETTINGS ===
  {
    id: 'trip-privacy',
    title: 'Trip Privacy',
    content: 'Choose who can see your itinerary - keep it private, share with friends, or make it visible to anyone with the list.',
    target: '[aria-label="Trip privacy settings"]',
    placement: 'bottom',
  },

  // === COMPLETE ===
  {
    id: 'trip-complete',
    title: 'You\'re All Set!',
    content: 'Your changes save automatically. Tap the help button anytime to see this guide again.',
    target: null,
    placement: 'center',
    isFinal: true,
  },
];

export const TripPlannerTutorialProvider = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [hasCompletedTutorial, setHasCompletedTutorial] = useState(() => {
    return localStorage.getItem(TRIP_TUTORIAL_COMPLETED_KEY) === 'true';
  });

  const currentStep = TRIP_TUTORIAL_STEPS[currentStepIndex];

  // Start the tutorial
  const startTutorial = useCallback(() => {
    setCurrentStepIndex(0);
    setIsActive(true);
  }, []);

  // End the tutorial (complete or skip)
  const endTutorial = useCallback((completed = false) => {
    setIsActive(false);
    setCurrentStepIndex(0);

    // Always mark as completed/seen when ending
    localStorage.setItem(TRIP_TUTORIAL_COMPLETED_KEY, 'true');
    setHasCompletedTutorial(true);
  }, []);

  // Go to next step
  const nextStep = useCallback(() => {
    if (currentStepIndex < TRIP_TUTORIAL_STEPS.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      endTutorial(true);
    }
  }, [currentStepIndex, endTutorial]);

  // Go to previous step
  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [currentStepIndex]);

  // Check if tutorial should auto-start (first time user)
  const shouldAutoStart = useCallback(() => {
    return !hasCompletedTutorial;
  }, [hasCompletedTutorial]);

  // Reset tutorial (for testing)
  const resetTutorial = useCallback(() => {
    localStorage.removeItem(TRIP_TUTORIAL_COMPLETED_KEY);
    setHasCompletedTutorial(false);
  }, []);

  const value = {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps: TRIP_TUTORIAL_STEPS.length,
    hasCompletedTutorial,
    startTutorial,
    endTutorial,
    nextStep,
    prevStep,
    shouldAutoStart,
    resetTutorial,
  };

  return (
    <TripPlannerTutorialContext.Provider value={value}>
      {children}
    </TripPlannerTutorialContext.Provider>
  );
};
